'use client';

import React, { useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Trash2, Pencil, Eye, Guitar, ChevronLeft, ChevronRight } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import { Guitar as GuitarType, GuitarStatus } from './mockData';
import StatusDropdown from './StatusDropdown';

interface GuitarTableProps {
  guitars: GuitarType[];
  selectedIds: Set<string>;
  onSelectChange: (ids: Set<string>) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, status: GuitarStatus) => void;
  sortKey: keyof GuitarType;
  sortDir: 'asc' | 'desc';
  onSort: (key: keyof GuitarType) => void;
  allIds: string[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  onPageSizeChange: (s: number) => void;
  emptySearch: boolean;
}

const statusBadgeVariant: Record<GuitarStatus, 'active' | 'on-loan' | 'for-sale' | 'archived'> = {
  active: 'active',
  'on-loan': 'on-loan',
  'for-sale': 'for-sale',
  archived: 'archived',
};

const statusLabel: Record<GuitarStatus, string> = {
  active: 'Active',
  'on-loan': 'On Loan',
  'for-sale': 'For Sale',
  archived: 'Archived',
};

const typeBadgeVariant: Record<string, 'electric' | 'acoustic' | 'bass' | 'classical'> = {
  Electric: 'electric',
  Acoustic: 'acoustic',
  Bass: 'bass',
  Classical: 'classical',
};

interface SortIconProps { colKey: keyof GuitarType; sortKey: keyof GuitarType; sortDir: 'asc' | 'desc'; }
function SortIcon({ colKey, sortKey, sortDir }: SortIconProps) {
  if (colKey !== sortKey) return <ChevronsUpDown size={13} className="text-muted-foreground/40" />;
  return sortDir === 'asc'
    ? <ChevronUp size={13} className="text-primary" />
    : <ChevronDown size={13} className="text-primary" />;
}

export default function GuitarTable({
  guitars,
  selectedIds,
  onSelectChange,
  onDelete,
  onStatusChange,
  sortKey,
  sortDir,
  onSort,
  allIds,
  page,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
  onPageSizeChange,
  emptySearch,
}: GuitarTableProps) {
  const [openStatusId, setOpenStatusId] = useState<string | null>(null);

  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
  const someSelected = allIds.some((id) => selectedIds.has(id)) && !allSelected;

  function toggleAll() {
    if (allSelected) {
      onSelectChange(new Set());
    } else {
      onSelectChange(new Set(allIds));
    }
  }

  function toggleOne(id: string) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectChange(next);
  }

  const pageSizeOptions = [5, 8, 15, 25];
  const startRow = (page - 1) * pageSize + 1;
  const endRow = Math.min(page * pageSize, totalCount);

  const columns: { key: keyof GuitarType; label: string; sortable: boolean }[] = [
    { key: 'brand', label: 'Brand', sortable: true },
    { key: 'model', label: 'Model', sortable: true },
    { key: 'type', label: 'Type', sortable: true },
    { key: 'color', label: 'Color', sortable: false },
    { key: 'serialNumber', label: 'Serial No.', sortable: false },
    { key: 'purchaseDate', label: 'Purchased', sortable: true },
    { key: 'purchaseLocation', label: 'Where Bought', sortable: false },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'lastMaintenance', label: 'Last Maintenance', sortable: true },
    { key: 'lastStringChange', label: 'Last Restring', sortable: true },
  ];

  if (guitars.length === 0) {
    return (
      <div className="bg-card border border-border rounded-2xl">
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
          <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
            <Guitar size={24} className="text-muted-foreground" />
          </div>
          {emptySearch ? (
            <>
              <h3 className="text-base font-700 text-foreground">No guitars match your filters</h3>
              <p className="text-sm text-muted-foreground mt-1.5 max-w-xs">
                Try adjusting your search terms, status, or brand filters to find what you&apos;re looking for.
              </p>
            </>
          ) : (
            <>
              <h3 className="text-base font-700 text-foreground">No guitars in your vault yet</h3>
              <p className="text-sm text-muted-foreground mt-1.5 max-w-xs">
                Your collection is empty. Add your first guitar to start building your vault.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="w-10 px-3 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAll}
                  className="w-4 h-4 rounded border-input accent-primary cursor-pointer"
                  aria-label="Select all guitars"
                />
              </th>
              {columns.map((col) => (
                <th key={`th-${col.key}`} className="px-3 py-3 text-left">
                  {col.sortable ? (
                    <button
                      onClick={() => onSort(col.key)}
                      className="flex items-center gap-1 text-[11px] font-700 uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors group"
                    >
                      {col.label}
                      <SortIcon colKey={col.key} sortKey={sortKey} sortDir={sortDir} />
                    </button>
                  ) : (
                    <span className="text-[11px] font-700 uppercase tracking-wider text-muted-foreground">
                      {col.label}
                    </span>
                  )}
                </th>
              ))}
              <th className="px-3 py-3 text-right">
                <span className="text-[11px] font-700 uppercase tracking-wider text-muted-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {guitars.map((guitar, rowIndex) => (
              <tr
                key={guitar.id}
                className={`hover:bg-muted/40 transition-colors group ${rowIndex % 2 === 0 ? '' : 'bg-muted/10'} ${selectedIds.has(guitar.id) ? 'bg-primary/5' : ''}`}
              >
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(guitar.id)}
                    onChange={() => toggleOne(guitar.id)}
                    className="w-4 h-4 rounded border-input accent-primary cursor-pointer"
                    aria-label={`Select ${guitar.brand} ${guitar.model}`}
                  />
                </td>
                <td className="px-3 py-3">
                  <span className="font-600 text-foreground">{guitar.brand}</span>
                </td>
                <td className="px-3 py-3">
                  <span className="text-foreground max-w-[160px] truncate block" title={guitar.model}>
                    {guitar.model}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <Badge variant={typeBadgeVariant[guitar.type] ?? 'neutral'}>{guitar.type}</Badge>
                </td>
                <td className="px-3 py-3">
                  <span className="text-muted-foreground">{guitar.color}</span>
                </td>
                <td className="px-3 py-3">
                  <span className="font-mono text-xs text-muted-foreground">{guitar.serialNumber}</span>
                </td>
                <td className="px-3 py-3">
                  <span className="text-muted-foreground tabular-nums">{guitar.purchaseDate}</span>
                </td>
                <td className="px-3 py-3">
                  <span className="text-muted-foreground max-w-[140px] truncate block" title={guitar.purchaseLocation}>
                    {guitar.purchaseLocation}
                  </span>
                </td>
                <td className="px-3 py-3 relative">
                  <div className="relative inline-block">
                    <button
                      onClick={() => setOpenStatusId(openStatusId === guitar.id ? null : guitar.id)}
                      className="hover:opacity-80 transition-opacity"
                      aria-label={`Change status: currently ${statusLabel[guitar.status]}`}
                    >
                      <Badge variant={statusBadgeVariant[guitar.status]}>
                        {statusLabel[guitar.status]}
                      </Badge>
                    </button>
                    {openStatusId === guitar.id && (
                      <StatusDropdown
                        currentStatus={guitar.status}
                        onSelect={(s) => {
                          onStatusChange(guitar.id, s);
                          setOpenStatusId(null);
                        }}
                        onClose={() => setOpenStatusId(null)}
                      />
                    )}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <span className="text-muted-foreground tabular-nums">{guitar.lastMaintenance}</span>
                </td>
                <td className="px-3 py-3">
                  <span className="text-muted-foreground tabular-nums">{guitar.lastStringChange}</span>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <button
                      title="View guitar details"
                      className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="View guitar details"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      title="Edit guitar record"
                      className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="Edit guitar record"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      title="Remove guitar from vault — this cannot be undone"
                      onClick={() => onDelete(guitar.id)}
                      className="p-1.5 rounded-lg hover:bg-negative/10 text-muted-foreground hover:text-negative transition-colors"
                      aria-label="Remove guitar from vault"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-border bg-muted/20">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-8 px-2 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer"
          >
            {pageSizeOptions.map((s) => (
              <option key={`pagesize-${s}`} value={s}>{s}</option>
            ))}
          </select>
          <span className="tabular-nums">{startRow}–{endRow} of {totalCount}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(1)}
            disabled={page === 1}
            className="px-2 py-1.5 rounded-lg text-xs font-600 text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="First page"
          >
            «
          </button>
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
            className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft size={14} />
          </button>
          {Array.from({ length: Math.min(totalPages, 7) }).map((_, i) => {
            const pageNum = i + 1;
            return (
              <button
                key={`page-btn-${pageNum}`}
                onClick={() => onPageChange(pageNum)}
                className={`w-8 h-8 rounded-lg text-xs font-600 transition-colors ${
                  page === pageNum
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
            className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Next page"
          >
            <ChevronRight size={14} />
          </button>
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={page === totalPages}
            className="px-2 py-1.5 rounded-lg text-xs font-600 text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Last page"
          >
            »
          </button>
        </div>
      </div>
    </div>
  );
}